// BOPIS collect lead time: resolves which Click & Collect timing scenario an
// item is in at a given store, and turns DC lead days into a collection date.
// Companion to the Collect Eligibility engine (collectEligibility.ts) — under
// BOPIS item type steers lead-time messaging instead of blocking. See CONTEXT.md.

export type CollectLeadTimes = {
  standardDispatchTime: number
  resizeEngravingDelayTime: number
  transitAndStoreProcessingTime: number
}

export type CollectScenario =
  // Store SOH fulfils the order: same-day/next-day copy is decided per store
  // (1pm cutoff + special hours) via getClickAndCollectMessageKey.
  | { kind: 'storeStock' }
  | { kind: 'shipToStore'; reason: 'standard' | 'specialOrder' | 'resizeEngrave'; leadDays: number }
  | { kind: 'notAvailable' }

export type CollectScenarioInput = {
  // Online/storefront ATS gate for engraved lines (DC engraves from online stock).
  storefrontInStock: boolean
  // The store's stock line from the middleware (source present when DC enrichment ran).
  stockLine?: Pick<StockResultStock, 'quantity' | 'source'>
  leadTimes: CollectLeadTimes
  // Custom vendor time (c_specialOrderLeadTime) for special-order/MTO SKUs.
  specialOrderLeadTime?: number
}

export const resolveCollectScenario = (view: CollectItemView, input: CollectScenarioInput): CollectScenario => {
  const { storefrontInStock, stockLine, leadTimes, specialOrderLeadTime = 0 } = input

  // Special order wins over everything: the vendor lead time already dominates,
  // so engraving/resize add nothing on top of it.
  if (view.isSpecialOrder) {
    return {
      kind: 'shipToStore',
      reason: 'specialOrder',
      leadDays: specialOrderLeadTime + leadTimes.transitAndStoreProcessingTime,
    }
  }

  // Engraving and resizing happen centrally at the DC, so these lines are always
  // DC-sourced — store SOH is never consulted. A resize implies the DC can make
  // the size; an engraving needs the storefront to have the item.
  if (view.isResized || view.isEngraved) {
    if (!view.isResized && !storefrontInStock) return { kind: 'notAvailable' }
    return {
      kind: 'shipToStore',
      reason: 'resizeEngrave',
      leadDays:
        leadTimes.resizeEngravingDelayTime + leadTimes.standardDispatchTime + leadTimes.transitAndStoreProcessingTime,
    }
  }

  if (!stockLine || stockLine.quantity <= 0) return { kind: 'notAvailable' }
  if (stockLine.source === 'dc') {
    return {
      kind: 'shipToStore',
      reason: 'standard',
      leadDays: leadTimes.standardDispatchTime + leadTimes.transitAndStoreProcessingTime,
    }
  }
  return { kind: 'storeStock' }
}

export type StockAllocation = 'store' | 'dc' | 'blocked'
export type StockPools = { store: number; dc: number }

// The two pools a stock line exposes: store SOH first, then the netted DC
// stock behind it (dcFallbackQuantity; the line's own quantity when dc-sourced).
export const stockPoolsFromLine = (
  stockLine?: Pick<StockResultStock, 'quantity' | 'source' | 'dcFallbackQuantity'>,
): StockPools => {
  if (!stockLine) return { store: 0, dc: 0 }
  if (stockLine.source === 'dc') return { store: 0, dc: stockLine.quantity }
  return { store: stockLine.quantity, dc: stockLine.dcFallbackQuantity ?? 0 }
}

// Store-first allocation of a SKU's stock-consuming cart lines, in basket
// order — one unit per line (the cart adds lines rather than raising a line's
// quantity). Decides each line's promise and feeds its fulfilment source
// stamp at pay intent (helpers/fulfilmentSource.ts).
export const allocateStockLines = (lineIds: string[], pools: StockPools): Map<string, StockAllocation> => {
  const allocations = new Map<string, StockAllocation>()
  let { store, dc } = pools
  for (const id of lineIds) {
    if (store > 0) {
      allocations.set(id, 'store')
      store--
    } else if (dc > 0) {
      allocations.set(id, 'dc')
      dc--
    } else {
      allocations.set(id, 'blocked')
    }
  }
  return allocations
}

export type CartLineCollectInput = Omit<CollectScenarioInput, 'leadTimes' | 'stockLine'> & {
  stockLine?: Pick<StockResultStock, 'quantity' | 'source' | 'dcFallbackQuantity'>
  itemId?: string
  // Ordered ids of this SKU's stock-consuming lines (stockConsumingLineIds).
  stockConsumingLineIds: string[]
  // Lead times never affect blocking, only the displayed date — callers that
  // only read `block` may omit them.
  leadTimes?: CollectLeadTimes
}

export type CartLineCollect = { scenario: CollectScenario; block: CartLineBlockReason | null }

const NO_LEAD_TIMES: CollectLeadTimes = {
  standardDispatchTime: 0,
  resizeEngravingDelayTime: 0,
  transitAndStoreProcessingTime: 0,
}

// Cart-line surface under BOPIS: item type no longer blocks — only the C&C
// availability gate and lines the pools cannot cover stop the bag/checkout.
export const resolveCartLineCollect = (view: CollectItemView, input: CartLineCollectInput): CartLineCollect => {
  const leadTimes = input.leadTimes ?? NO_LEAD_TIMES
  if (!view.availableForClickAndCollect) return { scenario: { kind: 'notAvailable' }, block: 'nonCncProduct' }

  // DC-produced lines (special order, resize, engraving) place no demand on
  // the stock line; the scenario engine's own gates apply.
  if (view.isSpecialOrder || view.isResized || view.isEngraved) {
    const scenario = resolveCollectScenario(view, { ...input, leadTimes })
    return { scenario, block: scenario.kind === 'notAvailable' ? 'outOfStock' : null }
  }

  const pools = stockPoolsFromLine(input.stockLine)
  const allocation = input.itemId ? allocateStockLines(input.stockConsumingLineIds, pools).get(input.itemId) : undefined
  if (!allocation || allocation === 'blocked') {
    return {
      scenario: { kind: 'notAvailable' },
      block: pools.store + pools.dc === 0 ? 'outOfStock' : 'overCartQuantity',
    }
  }
  // Rewrite the stock line to this line's allocated source so the scenario
  // resolver keeps owning the timing formulas.
  const scenario = resolveCollectScenario(view, { ...input, leadTimes, stockLine: { quantity: 1, source: allocation } })
  return { scenario, block: null }
}

// Grouped bag/checkout callout: collapses every line's collect result into one
// promise. Any blocked line turns the callout red; identical timings group to a
// single message, differing ones fall back to "collect items as they're ready".
export type CartCollectSummary =
  | { kind: 'notAvailable' }
  | { kind: 'storeStock' }
  | { kind: 'byDate'; leadDays: number }
  | { kind: 'mixed' }

export const summariseCartCollect = (lines: CartLineCollect[]): CartCollectSummary | null => {
  if (!lines.length) return null
  if (lines.some((line) => line.block || line.scenario.kind === 'notAvailable')) return { kind: 'notAvailable' }

  const scenarios = lines.map((line) => line.scenario)
  if (scenarios.every((scenario) => scenario.kind === 'storeStock')) return { kind: 'storeStock' }

  const shipDays = scenarios.map((scenario) => (scenario.kind === 'shipToStore' ? scenario.leadDays : null))
  const [first] = shipDays
  if (first != null && shipDays.every((days) => days === first)) return { kind: 'byDate', leadDays: first }

  return { kind: 'mixed' }
}

// Lead times are business days: count forward skipping weekends, starting today.
export const addBusinessDays = (start: Date, days: number): Date => {
  const date = new Date(start)
  let remaining = days
  while (remaining > 0) {
    date.setDate(date.getDate() + 1)
    const day = date.getDay()
    // 0 = Sunday, 6 = Saturday
    if (day !== 0 && day !== 6) remaining--
  }
  return date
}

const ordinalSuffix = (day: number): string => {
  if (day >= 11 && day <= 13) return 'th'
  switch (day % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

// "17th June" for English markets, "17 juin" for fr (no ordinal in French dates).
export const formatCollectionDate = (date: Date, locale: string): string => {
  const month = new Intl.DateTimeFormat(locale, { month: 'long' }).format(date)
  const day = date.getDate()
  return locale.startsWith('fr') ? `${day} ${month}` : `${day}${ordinalSuffix(day)} ${month}`
}

// My Bag/Checkout/Order Confirmation drop the trailing period these shared collect-message
// strings carry for PDP and the legacy (non-BOPIS) surfaces — strips it without touching the
// shared copy itself, so those other surfaces are unaffected.
export const stripTrailingPeriod = (text: string): string => text.replace(/\.$/, '')
